import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./AuthContext";
import { workerFetch } from "@/components/context/dashboard-shared";

interface Workspace {
  id: string;
  name: string;
  role: string;
  credits_plan?: string;
}

interface WorkspaceContextType {
  currentWorkspace: Workspace | null;
  workspaces: Workspace[];
  isLoading: boolean;
  refreshWorkspaces: () => Promise<void>;
  switchWorkspace: (workspaceId: string) => void;
}

const WorkspaceContext = createContext<WorkspaceContextType>({
  currentWorkspace: null,
  workspaces: [],
  isLoading: true,
  refreshWorkspaces: async () => {},
  switchWorkspace: () => {},
});

export const useWorkspace = () => useContext(WorkspaceContext);

export const WorkspaceProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const [currentWorkspace, setCurrentWorkspace] = useState<Workspace | null>(null);
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const loadWorkspaces = async () => {
    if (!user) {
      setWorkspaces([]);
      setCurrentWorkspace(null);
      setIsLoading(false);
      return;
    }

    try {
      console.log('[WorkspaceContext] Loading workspaces for user:', user.id);
      
      // Get all person_identities for this user across all workspaces
      const { data: identities, error: identityError } = await supabase
        .from('person_identities')
        .select('person_id, workspace_id')
        .eq('provider_user_id', user.id)
        .in('provider', ['supabase_auth', 'auth'])
        .eq('active', true);

      console.log('[WorkspaceContext] Person identities:', identities);

      if (identityError) {
        console.warn('[WorkspaceContext] Identity error (non-fatal):', identityError.message);
      }

      if (!identities || identities.length === 0) {
        console.log('[WorkspaceContext] No person identity found - redirecting to no-workspace');
        setIsLoading(false);
        return;
      }

      // Get all person_ids (user might have different person records in different workspaces)
      const personIds = [...new Set(identities.map(i => i.person_id))];
      console.log('[WorkspaceContext] Person IDs:', personIds);

      // Fetch user's workspaces from workspace_members table
      const { data: membershipData, error } = await supabase
        .from('workspace_members')
        .select(`
          workspace_id,
          workspace_role,
          workspaces:workspace_id (
            id,
            name,
            credits_plan,
            workspace_type
          )
        `)
        .in('person_id', personIds);

      console.log('[WorkspaceContext] Membership data:', membershipData);

      if (error) {
        console.error('[WorkspaceContext] Membership error:', error);
        // Preserve previous selection on transient errors
        setIsLoading(false);
        return;
      }

      const userWorkspaces: Workspace[] = (membershipData || [])
        .filter((m: any) => m.workspaces)
        .filter((m: any) => m.workspaces.workspace_type !== 'team') // Team workspaces accessed via Team tab, not switcher
        .map((m: any) => ({
          id: m.workspaces.id,
          name: m.workspaces.name,
          role: m.workspace_role,
          credits_plan: m.workspaces.credits_plan
        }));

      console.log('[WorkspaceContext] User workspaces:', userWorkspaces);
      setWorkspaces(userWorkspaces);

      const savedWorkspaceId = localStorage.getItem('currentWorkspaceId');
      let workspace: Workspace | null = null;
      if (savedWorkspaceId) {
        workspace = userWorkspaces.find(w => w.id === savedWorkspaceId) || null;
      }
      if (!workspace && userWorkspaces.length > 0) {
        workspace = userWorkspaces[0];
      }

      if (workspace) {
        setCurrentWorkspace(workspace);
        localStorage.setItem('currentWorkspaceId', workspace.id);
      } else {
        // Do NOT clear persisted selection on empty/transient results
        console.warn('[WorkspaceContext] No workspaces resolved; preserving previous selection');
      }
    } catch (error) {
      console.error('Error loading workspaces:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadWorkspaces();

    if (!user) return;

    // Subscribe to realtime changes for workspaces
    // This ensures that if a user is invited to a workspace or creates one,
    // the list updates immediately without a refresh.
    const channel = supabase
      .channel('workspace_updates')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'workspaces'
        },
        () => {
          console.log('[WorkspaceContext] Realtime update detected on workspaces table, refreshing...');
          loadWorkspaces();
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'workspace_members'
        },
        () => {
            console.log('[WorkspaceContext] Realtime update detected on workspace_members table, refreshing...');
            loadWorkspaces();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
    // Depend on user.id, NOT user object identity. Supabase fires
    // onAuthStateChange('TOKEN_REFRESHED', ...) every ~hour with a new
    // user object that is structurally identical except for the JWT —
    // depending on `user` here re-runs the effect on every refresh,
    // tearing down realtime subscriptions and re-loading workspaces,
    // which cascades into the team-tab-flashes-on-its-own bug.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);


  const refreshWorkspaces = async () => {
    await loadWorkspaces();
  };

  const switchWorkspace = (workspaceId: string) => {
    const target = workspaces.find(w => w.id === workspaceId);
    if (target) {
      setCurrentWorkspace(target);
      localStorage.setItem('currentWorkspaceId', target.id);
      console.log('[WorkspaceContext] Switched to workspace:', target.name);
    }
  };

  // 2026-05-22 — Prefetch the per-workspace graph so the Memory tab opens
  // instantly from cache instead of paying the cold-fetch every visit. The
  // worker's /context/graph-full sets ETag + Cache-Control: max-age=60,
  // stale-while-revalidate=300, so a follow-up request from the tab is
  // either served from browser cache (no network) or 304-revalidated
  // (<100ms). Fire-and-forget — failures are silent; the tab can still
  // load the graph itself when opened.
  //
  // Also prefetches when the document becomes visible after backgrounding,
  // so a user who keeps the app open all day sees fresh data when they
  // return without an explicit refresh.
  useEffect(() => {
    if (!currentWorkspace?.id) return;
    const workspaceId = currentWorkspace.id;

    const prefetch = () => {
      workerFetch("/context/graph-full", workspaceId).catch(() => {
        // Silent — prefetch is best-effort; the tab handles its own errors.
      });
    };

    // Immediate on workspace activation.
    prefetch();

    // Re-prefetch every 5 minutes while tab is in foreground, AND on every
    // foreground transition. The ETag check makes the steady-state cost
    // negligible (304 with empty body).
    const interval = setInterval(() => {
      if (document.visibilityState === "visible") prefetch();
    }, 5 * 60 * 1000);

    const onVisible = () => {
      if (document.visibilityState === "visible") prefetch();
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [currentWorkspace?.id]);

  return (
    <WorkspaceContext.Provider value={{
      currentWorkspace,
      workspaces,
      isLoading,
      refreshWorkspaces,
      switchWorkspace,
    }}>
      {children}
    </WorkspaceContext.Provider>
  );
};



